import { Controller } from "@hotwired/stimulus";

// Manages the entry-method chooser on the new blood test page. The page starts with two cards
// (upload / manual). Committing to either one hides the intro copy, reveals the
// "Test Information Required" panel, and smoothly collapses the other card.
export default class extends Controller {
  static targets = ["intro", "testInformation", "uploadColumn", "manualColumn"];

  enterUploadMode() {
    if (this.mode) return;
    this.mode = "upload";

    this.#showTestInformation();
    this.#collapse(this.manualColumnTarget);
    this.#center(this.uploadColumnTarget);
  }

  exitUploadMode() {
    if (this.mode !== "upload") return;
    this.mode = null;

    this.#showIntro();
    this.#expand(this.manualColumnTarget);
    this.#uncenter(this.uploadColumnTarget);
  }

  // The remaining card doesn't fill the row — it settles at a centred width with space either
  // side, per the design. An explicit width interpolates smoothly; a max-width cap would apply
  // instantly and make the expansion stall against it mid-transition.
  #center(column) {
    column.classList.remove("w-[calc(50%-2rem)]");
    column.classList.add("w-[60%]");
  }

  #uncenter(column) {
    column.classList.remove("w-[60%]");
    column.classList.add("w-[calc(50%-2rem)]");
  }

  // The intro and the panel share one grid cell sized by the taller of the two, so this swap is
  // a pure cross-fade — no heights change and the cards below keep their y position.
  #showTestInformation() {
    this.introTarget.classList.add("opacity-0", "invisible");
    this.testInformationTarget.classList.remove("opacity-0", "invisible");
  }

  #showIntro() {
    this.testInformationTarget.classList.add("opacity-0", "invisible");
    this.introTarget.classList.remove("opacity-0", "invisible");
  }

  #collapse(column) {
    // The card's contents fade out almost immediately (their own 150ms opacity transition), so
    // the slow part of the animation reads as an empty box shrinking rather than the text
    // squishing as the box narrows.
    column.querySelectorAll("[data-card-content]").forEach((content) => {
      content.classList.add("opacity-0");
    });

    column.classList.remove("w-[calc(50%-2rem)]", "ml-16");
    column.classList.add("w-0", "opacity-0", "overflow-hidden");
    column.addEventListener(
      "transitionend",
      () => column.classList.add("invisible"),
      { once: true }
    );
  }

  #expand(column) {
    column.classList.remove("w-0", "opacity-0", "overflow-hidden", "invisible");
    column.classList.add("w-[calc(50%-2rem)]", "ml-16");

    column.querySelectorAll("[data-card-content]").forEach((content) => {
      content.classList.remove("opacity-0");
    });
  }
}
